<script>
import MessageBubble from '@components/messages/message-bubble.vue';
import {
  authMethods,
  authComputed,
  clientComputed,
  clientMethods,
  sidebarComputed,
} from '@state/helpers';
import { helperMixin } from '@mixins/helper-mixin';
import _ from 'lodash';
import { connectToChannel, ChatData } from '@state/chatService';

export default {
  page: {
    title: 'Client Messages',
    meta: [{ name: 'description', content: 'The Client Messages view.' }],
  },
  props: {
    client: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      channel: {},
      messages: [],
      newMessage: '',
      isLoading: false,
      isVisible: false,
      errorFetchingClientChat: false,
    };
  },
  components: {
    MessageBubble,
  },
  mixins: [helperMixin],
  async beforeMount() {
    if (this.isRegistered) {
      this.isLoading = true;
      await this.initializeStreamChannel(this.client);
      this.isLoading = false;
    }
  },
  computed: {
    ...authComputed,
    ...clientComputed,
    ...sidebarComputed,
    _() {
      return _;
    },
    currentUserAvatar() {
      return (
        this.currentUser &&
        this.currentUser.trainer &&
        this.currentUser.trainer.mediaAssets.avatarImage.url
      );
    },
    isRegistered() {
      return (
        this.client.profile.lastSeen ||
        this.client.profile.last_seen ||
        (_.has(this.client.profile, 'successfulSignup') &&
          this.client.profile.successfulSignup)
      );
    },
  },
  async destroyed() {
    // Stop watching the channel and remove new message event listener
    if (!_.isEmpty(this.channel)) {
      await this.channel.stopWatching();
      this.channel.off('message.new', this.addMessage);
    }
  },
  mounted() {},
  methods: {
    ...clientMethods,
    ...authMethods,
    async initializeStreamChannel(client) {
      this.errorFetchingClientChat = false;

      try {
        await ChatData.init();
        const { channelMessages, channel, error } = await connectToChannel(
          client
        );
        this.channel = channel;
        this.messages = channelMessages;
        if (!_.isEmpty(this.channel) && !error) {
          this.errorFetchingClientChat = false;
          await this.channel.on('message.new', this.addMessage);
          this.isLoading = false;
          this.scrollToBottom('init');
        } else {
          this.channel = {};
          this.errorFetchingClientChat = true;
        }
      } catch (e) {
        console.log('Faild to fetch chat data');
      }
    },
    async addMessage(event) {
      this.messages.push(event.message);
      if (this.$route.name === 'clientMessages') {
        await this.channel.markRead();
        this.scrollToBottom();
      }
    },
    async sendMessage(e) {
      // Prevent sending empty messages and trim extra spaces
      if (this.newMessage.trim() !== '') {
        await this.channel.sendMessage({ text: this.newMessage.trim() });
        this.newMessage = '';
      } else {
        this.newMessage = '';
        e.target.value = '';
      }
    },
    scrollToBottom(val) {
      const child = document.getElementsByClassName('messages-wrapper')[0];
      const parent = document.getElementsByClassName('content-wrapper')[0];
      if (val === 'init' && parent) {
        parent.scrollTop = parent.scrollHeight;
      } else {
        if (child && parent) {
          parent.scroll({
            top: child.clientHeight,
            behavior: 'smooth',
          });
        }
      }
    },
    isMe(message) {
      return message.user.id !== this.client._id;
    },
  },
};
</script>

<template>
  <div>
    <div
      v-if="isRegistered && !errorFetchingClientChat"
      class="client-messages-container"
    >
      <div ref="contentWrapper" class="messages-wrapper">
        <div v-if="messages.length > 0">
          <div v-for="message in messages" :key="message.id">
            <MessageBubble
              :class="[{ isMe: isMe(message) }]"
              :image="
                isMe(message)
                  ? currentUserAvatar
                  : client.profile.mediaAssets.avatarImage.url
              "
              :content="message"
            />
          </div>
        </div>
        <div v-else>
          <div v-if="messages.length === 0 && !isLoading" class="empty-chat">
            This is the beginning of your chat history with
            {{ client.profile.firstName }}. Say hello!
          </div>
        </div>
      </div>
      <div
        class="message-input-wrapper"
        :style="
          `width: calc(100vw - ${isSidebarCollapsed ? '8.2rem' : '22rem'});`
        "
      >
        <BaseInputTextarea
          @keydown.enter="sendMessage($event)"
          v-model="newMessage"
          name="mesasge"
          placeholder="Start typing. Hit enter to post..."
        />
      </div>
    </div>
    <div
      v-if="isRegistered && !isLoading && errorFetchingClientChat"
      class="empty-chat not-init"
    >
      Error fetching {{ client.profile.firstName }}'s chat data.
    </div>
    <div v-if="!isRegistered && !isLoading" class="empty-chat not-init">
      Can't send a message before
      {{ client.profile.firstName }} signs up.
    </div>
    <div class="loading-wrapper" v-if="isLoading">
      <BaseLoadingSpinner />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.loading-wrapper {
  align-items: center;
  display: flex;
  height: 50vh;
  justify-content: center;
  margin-bottom: auto;
  margin-top: auto;
  width: 100%;
  z-index: 100;
}

.empty-chat {
  background-color: $color-off-white;
  background-color: $color-primary-alt;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  left: 35%;
  letter-spacing: 0.116667px;
  margin-bottom: 1rem;
  padding: 2rem;
  position: fixed;
  right: 25%;
  text-align: center;
  top: 79%;
  width: 40%;
  @include calc-font-size(0.8);

  &.not-init {
    top: 50%;
    @include calc-font-size(1.2);
  }
}

.empty-message--large {
  margin-top: 10%;
}

.client-messages-container {
  height: 100%;
  max-width: 100%;
  min-width: 100%;
  position: relative;
  .messages-wrapper {
    height: 100%;
    overflow: scroll;
    padding: 3rem 1rem 8rem 3rem;
  }

  .message-input-wrapper {
    bottom: 0;
    margin-bottom: 1rem;
    position: fixed;
    right: 1rem;
    transition: width 199.75ms linear;

    ::v-deep .input-textarea {
      background-color: white;
      border: 0;
      border-radius: 0;
      border-top: 3px solid $color-light-gray;
      height: 7rem;
      margin-bottom: 1rem;
    }
  }
}
</style>
